import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { OrganizationModal } from '@/components/crm/OrganizationModal';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { OrganisatieLijst } from '@/features/relaties/OrganisatieLijst';
import { filterNaarUrl, filterUitUrl, haalOrganisaties, type LijstFilter, type LijstOrganisatie, type RelatieClient } from '@/features/relaties/data/organisaties';
import { archiveerOrganisatie, ArchiveerWeigering } from '@/features/relaties/data/archiveren';
import { naRelatieWijziging, ORGANISATIES_SLEUTEL } from '@/features/relaties/data/sleutels';
import { useRechten } from '@/features/relaties/ui/Onderdelen';

/* /crm/organisaties. De lijst zelf staat in src/features/relaties/OrganisatieLijst.tsx;
   hier de gegevens, de filter in de URL (zodat terug van een organisatie de lijst
   herstelt), en de dialogen voor nieuw, bewerken en archiveren (ADR 0022). */

const client = supabase as unknown as RelatieClient;

export default function CRMOrganisaties() {
  const { magBewerken, magBeheren } = useRechten();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [modal, setModal] = useState<{ open: boolean; organisatie: Record<string, any> | null }>({ open: false, organisatie: null });

  const lijst = useQuery({ queryKey: [...ORGANISATIES_SLEUTEL, 'lijst'], queryFn: () => haalOrganisaties(client) });
  const filter = useMemo(() => filterUitUrl(searchParams), [searchParams]);
  const zetFilter = useCallback((f: LijstFilter) => setSearchParams(filterNaarUrl(f), { replace: true }), [setSearchParams]);

  const archiveer = async (o: LijstOrganisatie) => {
    try {
      await archiveerOrganisatie(client, o.id);
      toast({ title: 'Organisatie gearchiveerd', description: `"${o.naam}" staat in het Archief.` });
    } catch (e) {
      toast({
        title: e instanceof ArchiveerWeigering ? 'Niet gearchiveerd' : 'Archiveren mislukt',
        description: (e as Error)?.message ?? String(e),
        variant: e instanceof ArchiveerWeigering ? undefined : 'destructive',
      });
    }
    naRelatieWijziging(qc);
  };

  return (
    <>
      <OrganisatieLijst
        organisaties={lijst.data ?? []}
        laden={lijst.isPending}
        fout={lijst.error as Error | null}
        filter={filter}
        onFilter={zetFilter}
        magBewerken={magBewerken}
        magBeheren={magBeheren}
        onNieuw={() => setModal({ open: true, organisatie: null })}
        onBewerk={(o) => setModal({ open: true, organisatie: o.bron })}
        onArchiveer={archiveer}
      />
      {modal.open && (
        <OrganizationModal
          isOpen={modal.open}
          onClose={() => setModal({ open: false, organisatie: null })}
          organization={modal.organisatie as any}
          onSave={() => naRelatieWijziging(qc)}
        />
      )}
    </>
  );
}
